import { Badge, Skeleton } from '@/components/ui';
import { StatTile } from '@/components/ui/StatTile';
import { cx } from '@/lib/cx';
import { percent } from '@/lib/marketing';
import type { CampaignReport } from '@/lib/types';

/*
 * The headline counts of a campaign report. Every figure is copied off the
 * wire — nothing here derives a total from another one, so a tile can never
 * disagree with the server about what it counts.
 *
 * Opens are an ESTIMATE (`opensAreEstimates: true`): Apple Mail Privacy
 * Protection and corporate filters pre-load images, so a tracking pixel fires
 * for mail nobody read. The `est.` badge says so on the figure itself, with
 * the reason on its title — a fact about the data, not helper text.
 */

export const OPENS_ESTIMATE_REASON =
  'Estimated: Apple Mail privacy protection and corporate mail filters pre-load images, which records opens nobody made.';

type Totals = CampaignReport['totals'];

interface Tile {
  key: string;
  label: string;
  value: number;
  rate?: string;
  tone?: 'danger' | 'warning';
  estimate?: boolean;
}

function tilesOf(totals: Totals): Tile[] {
  return [
    { key: 'recipients', label: 'Recipients', value: totals.recipients },
    { key: 'sent', label: 'Sent', value: totals.sent },
    { key: 'delivered', label: 'Delivered', value: totals.delivered },
    {
      key: 'opens',
      label: 'Unique opens',
      value: totals.uniqueOpens,
      rate: percent(totals.openRate),
      estimate: true,
    },
    { key: 'clicks', label: 'Unique clicks', value: totals.uniqueClicks, rate: percent(totals.clickRate) },
    { key: 'unsubscribes', label: 'Unsubscribes', value: totals.unsubscribes, tone: 'warning' },
    { key: 'bounces', label: 'Bounces', value: totals.bounces, tone: 'danger' },
    { key: 'replies', label: 'Replies', value: totals.replies },
    { key: 'failed', label: 'Failed', value: totals.failed, tone: 'danger' },
  ];
}

/*
 * Nine tiles: three across from `sm` so the set closes as a full 3×3 block,
 * and two across on a phone, where the ninth spans the row rather than leaving
 * an empty slot beside it.
 */
const GRID =
  'm-0 grid list-none grid-cols-2 gap-2 p-0 sm:grid-cols-3 [&>li:last-child]:col-span-2 sm:[&>li:last-child]:col-span-1';

export function ReportStatTiles({ totals }: { totals: Totals | null }) {
  if (totals === null) {
    return (
      <ul className={GRID} aria-hidden="true">
        {Array.from({ length: 9 }, (_, i) => (
          <li key={i} className="min-w-0">
            <StatTile className="h-full" label={<Skeleton className="h-2.5 w-20" />} value={<Skeleton className="h-6 w-14" />} />
          </li>
        ))}
      </ul>
    );
  }

  return (
    <ul className={GRID} aria-label="Campaign totals">
      {tilesOf(totals).map((tile) => (
        <li key={tile.key} className="min-w-0">
          <StatTile
            className="h-full"
            label={
              <span className="flex flex-wrap items-center gap-1.5">
                {tile.label}
                {tile.estimate && (
                  <Badge tone="neutral" variant="outline" title={OPENS_ESTIMATE_REASON}>
                    est.
                  </Badge>
                )}
              </span>
            }
            value={
              <span
                className={cx(
                  tile.value > 0 && tile.tone === 'danger' && 'text-danger',
                  tile.value > 0 && tile.tone === 'warning' && 'text-warning',
                )}
              >
                {tile.value.toLocaleString()}
              </span>
            }
            detail={
              tile.rate !== undefined ? (
                <span className="tabular" title={tile.estimate ? OPENS_ESTIMATE_REASON : undefined}>
                  {tile.rate}
                </span>
              ) : undefined
            }
          />
        </li>
      ))}
    </ul>
  );
}
